import { useEffect, useRef, useState } from 'react';
import {
  EXTRA_LANGUAGE_DETECTION_SUBSET,
  getLanguageSupport,
  LANG_DETECT_MIN_CHARS,
  normalizeDetectedLanguage,
  SUPPORTED_TEXT_TO_AUDIO_LANGUAGES,
  type SupportedTextToAudioLanguage,
} from '@listening/frontend/tts/language-support';

export type LanguageDetection =
  | { status: 'idle' }
  | { status: 'supported'; lang: SupportedTextToAudioLanguage }
  | { status: 'unsupported'; lang: string };

const IDLE: LanguageDetection = { status: 'idle' };

export function useLanguageDetection(text: string) {
  const [detection, setDetection] = useState(IDLE);
  const trimmed = text.trim();
  if (trimmed.length < LANG_DETECT_MIN_CHARS && detection !== IDLE) {
    setDetection(IDLE);
  }
  const eldRef = useRef<Awaited<typeof import('eld/small')>['eld'] | null>(
    null,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    if (trimmed.length < LANG_DETECT_MIN_CHARS) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void (async () => {
        if (!eldRef.current) {
          // dynamic-import -- eld/small carries 1.5 MB of n-gram data and is only needed after enough text is entered.
          const mod = await import('eld/small');
          if (requestId !== requestIdRef.current) return;

          eldRef.current = mod.eld;
          eldRef.current.setLanguageSubset([
            ...SUPPORTED_TEXT_TO_AUDIO_LANGUAGES,
            ...EXTRA_LANGUAGE_DETECTION_SUBSET,
          ]);
        }

        const result = eldRef.current.detect(trimmed);
        if (requestId !== requestIdRef.current) return;

        if (!result.isReliable()) {
          setDetection(IDLE);
          return;
        }

        const support = getLanguageSupport(
          normalizeDetectedLanguage(result.language),
        );
        if (support.status === 'supported') {
          setDetection({ status: 'supported', lang: support.lang });
          return;
        }

        setDetection({ status: 'unsupported', lang: support.lang });
      })();
    }, 500);

    return () => clearTimeout(timerRef.current);
  }, [trimmed]);

  return detection;
}
